'use client'

import { TPlay } from '@/components/ui/tls/t-play'
import { announce } from '@/lib/announce'
import { cn } from '@/lib/utils'
import { useVoiceSamplePlayer } from './use-voice-sample-player'

interface VoiceInfo {
  id: string
  name: string
  description: string
  /** Same-origin URL of the static sample. Absent in a mocked or older response. */
  sample?: string
}

interface VoiceSelectorProps {
  voices: VoiceInfo[]
  selectedVoiceId: string
  onSelect: (voiceId: string) => void
}

// One row per voice: a radio to pick it, its name and descriptor, and a play
// control for its sample. Hairlines separate the rows; no card, no border on
// the content (PRODUCT.md, "media-led, not card-led"). Picking and previewing
// are separate actions, so a visitor can hear every voice before choosing one.
export function VoiceSelector({ voices, selectedVoiceId, onSelect }: VoiceSelectorProps) {
  const { playingId, failedId, toggle } = useVoiceSamplePlayer()

  if (voices.length === 0) {
    return <p className="text-sm text-[var(--tls-fg-muted)]">Loading voices…</p>
  }

  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend className="sr-only">Voice</legend>
      <ul className="m-0 list-none border-b border-[color:var(--tls-border-subtle)] p-0">
        {voices.map(voice => {
          const selected = voice.id === selectedVoiceId
          const failed = failedId === voice.id
          return (
            <li
              key={voice.id}
              className={cn(
                'flex items-center gap-2 border-t border-[color:var(--tls-border-subtle)] pr-1',
                selected && 'bg-[var(--tls-tint-selected)]'
              )}
            >
              <label className="flex min-h-[56px] min-w-0 flex-1 cursor-pointer items-center gap-3 py-2 pl-3">
                <input
                  type="radio"
                  name="voice"
                  value={voice.id}
                  checked={selected}
                  onChange={() => {
                    onSelect(voice.id)
                    announce(`${voice.name}. ${voice.description}`, 'polite')
                  }}
                  className="tlsr-focus size-5 shrink-0 accent-[var(--tls-brand)]"
                />
                <span className="flex min-w-0 flex-col sm:flex-row sm:items-baseline sm:gap-3">
                  <span className="font-semibold text-[var(--tls-fg-strong)]">{voice.name}</span>
                  <span className="text-sm text-[var(--tls-fg-muted)]">{voice.description}</span>
                </span>
              </label>
              {voice.sample && (
                <TPlay
                  size={44}
                  title={`${voice.name} sample`}
                  playing={playingId === voice.id}
                  onClick={() => toggle(voice.id, voice.sample as string)}
                />
              )}
              {failed && (
                <span role="status" className="text-sm font-medium text-[var(--tls-fg-strong)]">
                  Sample did not load.
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </fieldset>
  )
}
